import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { ACCOUNT_COLORS } from '@/constants/residents/mobile/accountColors';
import { accountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { strings } from '@/constants/residents/strings';
import { useAppSelector } from '@/store';
import { selectLeaseIds, selectAllLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetAutoPayStatusQuery, useCancelAutoPayMutation, useGetLeaseQuery, useGetPaymentMethodsQuery } from '@/store/api';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { AutopayUserCard } from '@/components/residents/autopay/AutopayUserCard';
import { useToast } from '@/contexts/admin/ToastContext';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { AccountEmptyState } from '@/components/residents/account/AccountEmptyState';

// Figma uses connections icon for empty state
const ConnectionIcon = RESIDENTS_SVG_COMPONENTS.CONNECTION;

/**
 * Mobile Autopay Section
 * Displays autopay settings for all leases
 * Based on Figma design for Autopay section in Account settings
 */
export const MobileAutopaySection: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [setupModalLeaseId, setSetupModalLeaseId] = useState<number | null>(null);

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const entrataDisabled = useEntrataIntegrationDisabled();

  // Combine all lease IDs (own + connected)
  const allLeaseIds = useMemo(() => {
    const combined = [...ownLeaseIds];
    connectionLeaseIds.forEach((id) => {
      if (!combined.includes(id)) combined.push(id);
    });
    return combined;
  }, [ownLeaseIds, connectionLeaseIds]);

  // Map connection users by lease ID
  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  // Fetch autopay status
  const { refetch: autoPayStatusRefetch, isFetching } = useGetAutoPayStatusQuery();
  
  // Fetch payment methods for labels
  const { data: methodsData } = useGetPaymentMethodsQuery();
  const paymentMethods: any[] = useMemo(() => {
    return methodsData?.response || [];
  }, [methodsData]);

  const [cancelAutopay] = useCancelAutoPayMutation();

  const handleSetupAutopay = (leaseId: number) => {
    setSetupModalLeaseId(leaseId);
  };

  const handleCloseSetupModal = () => {
    setSetupModalLeaseId(null);
  };

  const handleAutopaySetup = () => {
    handleRefresh();
  };

  // Handle remove autopay - Figma shows "Autopay settings for {name} has been successfully removed!"
  const handleRemoveAutopay = async (autopayId: number, displayName?: string) => {
    if (!autopayId) {
      showError('Failed to remove autopay');
      return;
    }
    
    try {
      await cancelAutopay(autopayId).unwrap();
      const successMessage = displayName 
        ? `Autopay settings for ${displayName} has been successfully removed!`
        : strings.account.autopayRemovedToast;
      showSuccess(successMessage);
      handleRefresh();
    } catch (error: any) {
      showError(error?.data?.message || error?.data?.error?.message || 'Failed to remove autopay');
      throw error;
    }
  };

  const handleRefresh = useCallback(() => {
    autoPayStatusRefetch();
    setRefreshNonce((n) => n + 1);
  }, [autoPayStatusRefetch]);

  useFocusEffect(
    useCallback(() => {
      handleRefresh();
      return () => {};
    }, [handleRefresh])
  );

  const hasNoLeases = allLeaseIds.length === 0 && !connectionsFetchLoading;

  if (entrataDisabled) {
    return (
      <View style={accountSectionStyles.container}>
        <View style={accountSectionStyles.header}>
          <Text style={accountSectionStyles.headerTitle}>{strings.account.autopayTitle}</Text>
        </View>
        <View style={styles.maintenanceContainer}>
          <Text style={styles.maintenanceText}>Autopay is temporarily unavailable</Text>
        </View>
      </View>
    );
  }

  return (
    <>
      <View style={accountSectionStyles.container}>
        {/* Header - Figma: "Autopay", 18px semibold */}
        <View style={accountSectionStyles.header}>
          <Text style={accountSectionStyles.headerTitle}>{strings.account.autopayTitle}</Text>
        </View>

        {/* Content */}
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isFetching}
              onRefresh={handleRefresh}
              tintColor={ACCOUNT_COLORS.navActiveBackground}
            />
          }
        >
          {connectionsFetchLoading || isFetching ? (
            <View style={accountSectionStyles.loadingContainer}>
              <ActivityIndicator size="large" color={ACCOUNT_COLORS.navActiveBackground} />
            </View>
          ) : hasNoLeases ? (
            <AccountEmptyState
              icon={ConnectionIcon && <ConnectionIcon width={68} height={68} color="#757575" />}
              title={strings.account.autopayEmptyTitle}
              message={strings.account.autopayEmptyMessage}
              messageMaxWidth={240}
            />
          ) : (
            <View style={styles.leasesList}>
              {allLeaseIds.map((leaseId, index) => (
                <LeaseAutopayItem
                  key={leaseId}
                  leaseId={leaseId}
                  isOwnLease={ownLeaseIds.includes(leaseId)}
                  connectionUser={connectionUserByLeaseId.get(leaseId)}
                  authUser={authUser}
                  paymentMethods={paymentMethods}
                  onSetupAutopay={() => handleSetupAutopay(leaseId)}
                  onRemoveAutopay={handleRemoveAutopay}
                  onAutopayUpdated={handleRefresh}
                  refreshNonce={refreshNonce}
                  showDivider={index < allLeaseIds.length - 1}
                />
              ))}
            </View>
          )}
        </ScrollView>
      </View>

      {/* Autopay Setup Modal */}
      {setupModalLeaseId !== null && (
        <MobileAutopayDetailsModal
          visible={setupModalLeaseId !== null}
          onClose={handleCloseSetupModal}
          leaseId={setupModalLeaseId}
          onAutopayUpdated={handleAutopaySetup}
          mode="setup"
        />
      )}
    </>
  );
};

// Individual lease item component that fetches its own data
interface LeaseAutopayItemProps {
  leaseId: number;
  isOwnLease: boolean;
  connectionUser?: any;
  authUser?: any;
  paymentMethods: any[];
  onSetupAutopay: () => void;
  onRemoveAutopay: (autopayId: number, displayName?: string) => Promise<void>;
  onAutopayUpdated: () => void;
  refreshNonce: number;
  showDivider: boolean;
}

const LeaseAutopayItem: React.FC<LeaseAutopayItemProps> = ({
  leaseId,
  isOwnLease,
  connectionUser,
  authUser,
  paymentMethods,
  onSetupAutopay,
  onRemoveAutopay,
  onAutopayUpdated,
  refreshNonce,
  showDivider,
}) => {
  const { data: leaseResp, refetch } = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId }
  );

  React.useEffect(() => {
    refetch();
  }, [refreshNonce, refetch]);

  const lease = (leaseResp as any)?.response?.lease;

  const propertyName = lease?.property?.marketingName || '—';
  const users = lease?.users || [];
  const primaryUser = users.find((u: any) => u?.LeaseCustomer?.customerType === 'primary');
  
  // Determine display user
  const displayUser = isOwnLease
    ? (users.find((u: any) => u?.id === authUser?.id) || primaryUser || users[0])
    : (connectionUser || primaryUser || users[0]);

  const firstName = displayUser?.firstName;
  const lastName = displayUser?.lastName;
  const photoUrl = displayUser?.photoUrl;

  // Get autopay data
  const recurringPayments = lease?.recurringPayments || [];
  const autopay = recurringPayments[0] ? {
    id: recurringPayments[0].id,
    amount: recurringPayments[0].amount ? Number(recurringPayments[0].amount) : undefined,
    isFullAmount: !recurringPayments[0].amount || recurringPayments[0].payFullAmount,
    startDate: recurringPayments[0].startDate || recurringPayments[0].createdAt,
    endDate: recurringPayments[0].endDate,
    sourceId: recurringPayments[0].sourceId ? Number(recurringPayments[0].sourceId) : undefined,
  } : null;

  // Get payment method label
  const paymentMethodLabel = useMemo(() => {
    if (!autopay?.sourceId) return '—';
    const method = paymentMethods.find((m: any) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === autopay.sourceId;
    });
    if (method) {
      return getPaymentMethodShortLabel(method);
    }
    return '—';
  }, [autopay?.sourceId, paymentMethods]);

  // Check if setup is disabled (lease ended)
  const isSetupDisabled = useMemo(() => {
    const endDate = lease?.endDate ? new Date(lease.endDate) : null;
    if (!endDate || isNaN(endDate.getTime())) return false;
    const firstOfEndMonth = new Date(endDate);
    firstOfEndMonth.setDate(1);
    return firstOfEndMonth < new Date();
  }, [lease?.endDate]);

  if (!lease) return null;

  return (
    <>
      <AutopayUserCard
        leaseId={leaseId}
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        propertyName={propertyName}
        autopay={autopay}
        paymentMethodLabel={paymentMethodLabel}
        onSetupAutopay={onSetupAutopay}
        onRemoveAutopay={onRemoveAutopay}
        onAutopayUpdated={onAutopayUpdated}
        isSetupDisabled={isSetupDisabled}
      />
      {showDivider && <View style={accountSectionStyles.divider} />}
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  leasesList: {
    flex: 1,
  },
  // Maintenance state
  maintenanceContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  maintenanceText: {
    fontSize: 16,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    textAlign: 'center',
  },
});

export default MobileAutopaySection;
